import React, { useState, useEffect } from 'react';
import donneesApi from '../services/donnees';
import ExplorateurFg from './ExplorateurFg';
import './Donnees.css';

// ─── Données — les sources de l'entrepôt A97 DATA ────────────────────────────
// Premier étage du chantier « analyses IA » : l'état des lieux de ce que
// Finance a le droit de lire (vues marts de l'entrepôt, lecture seule).
// Chaque carte dit : combien de lignes, quelle période, quelle fraîcheur.
// L'aperçu (50 dernières lignes) sert à se faire l'œil sur les colonnes
// avant de configurer une analyse.

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function formatDateTime(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

function fraicheurBadge(ingestion) {
  if (!ingestion) return { label: 'jamais ingérée', cls: 'dn-badge-warn' };
  if (ingestion.statut === 'echec') return { label: 'échec ingestion', cls: 'dn-badge-err' };
  const heures = (Date.now() - new Date(ingestion.demarre_le).getTime()) / 3600000;
  if (heures <= 30) return { label: `à jour (${formatDateTime(ingestion.demarre_le)})`, cls: 'dn-badge-ok' };
  if (heures <= 24 * 4) return { label: `il y a ${Math.round(heures / 24)} j`, cls: 'dn-badge-warn' };
  return { label: `en retard (${formatDateTime(ingestion.demarre_le)})`, cls: 'dn-badge-err' };
}

// Rendu markdown minimal (titres, gras, tableaux, listes) — pas de lib :
// le rapport vient de notre propre backend, le HTML reste sous contrôle.
function mdVersHtml(md) {
  const esc = (t) => t.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const lignes = esc(md).split('\n');
  const out = [];
  let enTable = false, enListe = false;
  const fermer = () => {
    if (enTable) { out.push('</tbody></table>'); enTable = false; }
    if (enListe) { out.push('</ul>'); enListe = false; }
  };
  const inline = (t) => t
    .replace(/\*\*(.+?)\*\*/g, '<b>$1</b>')
    .replace(/(^|[^*])\*([^*]+)\*/g, '$1<i>$2</i>');
  for (let i = 0; i < lignes.length; i++) {
    const l = lignes[i];
    if (/^\s*\|/.test(l)) {
      const cellules = l.replace(/^\s*\||\|\s*$/g, '').split('|').map(c => c.trim());
      if (cellules.every(c => /^:?-{2,}:?$/.test(c))) continue; // ligne séparatrice
      if (!enTable) { fermer(); out.push('<table class="an-table"><tbody>'); enTable = true; }
      const tag = (i + 1 < lignes.length && /^\s*\|[\s:|-]+\|\s*$/.test(lignes[i + 1])) ? 'th' : 'td';
      out.push('<tr>' + cellules.map(c => `<${tag}>${inline(c)}</${tag}>`).join('') + '</tr>');
      continue;
    }
    if (/^\s*[-*] /.test(l)) {
      if (!enListe) { fermer(); out.push('<ul>'); enListe = true; }
      out.push('<li>' + inline(l.replace(/^\s*[-*] /, '')) + '</li>');
      continue;
    }
    fermer();
    const h = l.match(/^(#{1,4}) (.*)/);
    if (h) { out.push(`<h${h[1].length + 1}>${inline(h[2])}</h${h[1].length + 1}>`); continue; }
    if (l.trim() === '') { out.push(''); continue; }
    out.push('<p>' + inline(l) + '</p>');
  }
  fermer();
  return out.join('\n');
}

export default function Donnees() {
  const [sources, setSources] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [apercu, setApercu] = useState(null);   // { source, colonnes, lignes }
  const [apercuCharge, setApercuCharge] = useState(false);
  const [analyses, setAnalyses] = useState([]);
  const [explorateurFg, setExplorateurFg] = useState(false);
  const [analyseEnCours, setAnalyseEnCours] = useState(false);
  const [rapport, setRapport] = useState(null); // { rapport_md, modele, cout_usd, cree_le }

  const chargerAnalyses = () => {
    donneesApi.getAnalyses('vn').then(r => setAnalyses(r.data.analyses || [])).catch(() => {});
  };

  useEffect(() => {
    donneesApi.getSources()
      .then(r => setSources(r.data.sources || []))
      .catch(e => setErreur(e.response?.data?.error || e.message));
    chargerAnalyses();
  }, []);

  const lancerAnalyseVn = async () => {
    if (analyseEnCours) return;
    setAnalyseEnCours(true);
    try {
      const r = await donneesApi.analyserVn();
      setRapport(r.data);
      chargerAnalyses();
    } catch (e) {
      alert(e.response?.data?.error || e.message);
    } finally {
      setAnalyseEnCours(false);
    }
  };

  const ouvrirAnalyse = async (id) => {
    try {
      const r = await donneesApi.getAnalyse(id);
      setRapport(r.data.analyse);
    } catch (e) { alert(e.response?.data?.error || e.message); }
  };

  const ouvrirApercu = async (s) => {
    setApercuCharge(true);
    setApercu({ source: s, colonnes: [], lignes: [] });
    try {
      const r = await donneesApi.getApercu(s.code);
      setApercu({ source: s, colonnes: r.data.colonnes, lignes: r.data.lignes });
    } catch (e) {
      setApercu(null);
      alert(e.response?.data?.error || e.message);
    } finally {
      setApercuCharge(false);
    }
  };

  if (erreur) {
    return (
      <div className="dn-page">
        <div className="dn-erreur">
          <b>Entrepôt A97 DATA injoignable.</b>
          <p>{erreur}</p>
        </div>
      </div>
    );
  }
  if (!sources) return <div className="dn-page"><div className="dn-chargement">Chargement des sources…</div></div>;

  return (
    <div className="dn-page">
      <p className="dn-intro">
        Sources lues dans l'entrepôt <b>A97 DATA</b> (lecture seule, vues consolidées).
        C'est la matière première des analyses financières : VN, VO, APV, Pièces.
      </p>

      <div className="dn-grille">
        {sources.map(s => {
          const badge = s.disponible ? fraicheurBadge(s.ingestion) : { label: 'indisponible', cls: 'dn-badge-err' };
          return (
            <div key={s.code} className="dn-carte">
              <div className="dn-carte-tete">
                <span className="dn-carte-titre">{s.libelle}</span>
                <span className={`dn-badge ${badge.cls}`}>{badge.label}</span>
              </div>
              <div className="dn-carte-volets">{s.volets}</div>
              {s.disponible ? (
                <>
                  <div className="dn-carte-stats">
                    <span><b>{Number(s.lignes).toLocaleString('fr-FR')}</b> lignes</span>
                    <span>{formatDate(s.date_min)} → {formatDate(s.date_max)}</span>
                  </div>
                  <p className="dn-carte-note">{s.note}</p>
                  <div className="dn-carte-actions">
                    <button className="dn-btn-apercu" onClick={() => ouvrirApercu(s)}>
                      Aperçu des colonnes →
                    </button>
                    {s.code === 'ventes_vn' && (
                      <button className="dn-btn-analyser" onClick={lancerAnalyseVn} disabled={analyseEnCours}>
                        {analyseEnCours ? 'Analyse en cours… (1 à 2 min)' : '🤖 Rapport du contrôleur de gestion'}
                      </button>
                    )}
                    {s.code === 'grand_livre_fg' && (
                      <button className="dn-btn-analyser" onClick={() => setExplorateurFg(true)}>
                        🔎 Explorer les fournisseurs
                      </button>
                    )}
                  </div>
                </>
              ) : (
                <p className="dn-carte-note dn-carte-note-err">{s.erreur}</p>
              )}
            </div>
          );
        })}
      </div>

      {explorateurFg && <ExplorateurFg onClose={() => setExplorateurFg(false)} />}

      {analyses.length > 0 && (
        <div className="dn-historique">
          <h3>Analyses générées</h3>
          {analyses.map(a => (
            <button key={a.id} className="dn-historique-ligne" onClick={() => ouvrirAnalyse(a.id)}>
              <b>VN</b>
              <span>{new Date(a.cree_le).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
              <span className="dn-historique-meta">{a.modele}{a.cout_usd ? ` · ${Number(a.cout_usd).toFixed(2)} $` : ''}{a.cree_par_nom ? ` · ${a.cree_par_nom}` : ''}</span>
            </button>
          ))}
        </div>
      )}

      {rapport && (
        <div className="dn-apercu-overlay" onClick={() => setRapport(null)}>
          <div className="dn-apercu dn-rapport" onClick={e => e.stopPropagation()}>
            <div className="dn-apercu-tete">
              <b>Rapport VN — contrôleur de gestion</b>
              <span>{rapport.modele}{rapport.cout_usd ? ` · coût ${Number(rapport.cout_usd).toFixed(2)} $` : ''}</span>
              <button className="dn-apercu-fermer" onClick={() => setRapport(null)}>✕</button>
            </div>
            <div className="dn-apercu-scroll dn-rapport-corps"
                 dangerouslySetInnerHTML={{ __html: mdVersHtml(rapport.rapport_md || '') }} />
          </div>
        </div>
      )}

      {apercu && (
        <div className="dn-apercu-overlay" onClick={() => setApercu(null)}>
          <div className="dn-apercu" onClick={e => e.stopPropagation()}>
            <div className="dn-apercu-tete">
              <b>{apercu.source.libelle}</b>
              <span>50 lignes les plus récentes</span>
              <button className="dn-apercu-fermer" onClick={() => setApercu(null)}>✕</button>
            </div>
            {apercuCharge ? (
              <div className="dn-chargement">Chargement…</div>
            ) : (
              <div className="dn-apercu-scroll">
                <table className="dn-table">
                  <thead>
                    <tr>{apercu.colonnes.map(c => <th key={c}>{c}</th>)}</tr>
                  </thead>
                  <tbody>
                    {apercu.lignes.map((l, i) => (
                      <tr key={i}>
                        {apercu.colonnes.map(c => <td key={c}>{l[c] === null ? '' : String(l[c])}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
